import { AlertDefaults, AlertSettingsVariable, BasicMessage, MessageSettings } from 'src/app/hickory-api';
import { HtmlHelper } from 'src/app/shared/html-helper/html-helper';
import { AlertModelService } from '../alert-model.service';
import { AlertChannelsHelper, AlertSocialHelper, SocialChannel } from '..';
import { isDefined, toCamelCase } from 'src/app/shared/model/utility';
import { AlertMicrosoftTeamsHelper } from './alert-microsoft-teams-helper';
import { CharacterCountValidator } from 'src/app/shared/validators/characterCountValidator';

export class AlertMessageHelper {
  static initMessage(message?: MessageSettings): MessageSettings {
    if (!message) message = {};
    if (!message.basic) message.basic = {};
    this.initBasicMessage(message.basic);

    return message;
  }

  static initBasicMessage(basic?: BasicMessage): BasicMessage {
    if (!basic) basic = {};
    if (!basic.subject) basic.subject = '';
    if (!basic.message) basic.message = '';
    if (!basic.messageBody) basic.messageBody = '';
    if (!basic.messageRichContent) basic.messageRichContent = null;
    return basic;
  }

  static ensureCustomMessageVariables(message: MessageSettings, enabled?: boolean | undefined | null): MessageSettings {
    message = this.initMessage(message);
    if (!message.customMessageVariables) message.customMessageVariables = { };
    if (isDefined(enabled)) message.customMessageVariables.useMessageVariables = enabled;
    return message;
  }

  static postProcess(service: AlertModelService, alert: AlertDefaults = service.alertSettings) {
    this.backfillMessageBodyOnModel(alert?.message, service.isRarf);
    this.backfillMessageOnModel(alert?.message, service.isRarf);

    // if we have a default subject from our tenant or default brand, use that instead of an empty/null value
    if (!alert.message.basic.subject) alert.message.basic.subject = service.alertDefaults?.message?.basic?.subject;

    // for RARF alerts, null out the messageRichContent (original message) so that users can edit without affecting
    // the original message text. we'll still have a copy in the alert defaults that we can apply before sending
    if (service.isRarf) alert.message.basic.messageRichContent = undefined;

    // set public content defaults
    this.backfillPublicContent(service, alert?.message);
  }

  static preparePreview(service: AlertModelService, preview: AlertDefaults) {
    // ROAD-6328: apply image width/height attributes for email client compatibility
    const basic = preview.message?.basic;
    if (basic) {
      if (basic?.messageBody) {
        basic.messageBody = HtmlHelper.customSanitize(basic.messageBody);
        basic.messageBody = HtmlHelper.fixImgSizeCompatibility(basic.messageBody);
      }
      if (preview.message?.basic?.messageRichContent) {
        basic.messageRichContent = HtmlHelper.fixImgSizeCompatibility(basic.messageRichContent);
      }
    }

    // ROAD-6384: show a read-only version of the original message on the send alert form
    if (service.isRarf) {
      let userMessage = preview.message.basic.messageRichContent ?? '';
      // add a horizontal separator if there is user-authored HTML
      if (userMessage.length) userMessage += '<hr>';
      // append the original RARF message to the end of the messageRichContent
      preview.message.basic.messageRichContent = userMessage + service.rarfMessage;
    }
  }

  static fixHtml(message: MessageSettings) {
    if (message?.basic?.messageBody?.length) {
      message.basic.messageBody = HtmlHelper.applyFixesToHtml(message.basic.messageBody);
    }
    if (message?.basic?.messageRichContent?.length) {
      message.basic.messageRichContent = HtmlHelper.applyFixesToHtml(message.basic.messageRichContent);
    }
  }

  static backfillMessageBodyOnModel(message: MessageSettings, isRarf: boolean) {
    this.initMessage(message);

    const basicMessage = message?.basic;
    // if messageBody is null or empty
    if (!basicMessage?.messageBody?.length) {
      let messageBody = '';

      // if plain text message is present, convert it to html and add it to the (empty) messageBody
      if (basicMessage.message?.length) {
        messageBody += HtmlHelper.convertPlainTextToHtml(basicMessage.message);
      }

      // if the legacy rich content is present, append it to the messageBody
      if (!isRarf && basicMessage.messageRichContent?.length) {
        if (messageBody.length) {
          messageBody += '<br/>';
        }
        messageBody += basicMessage.messageRichContent;
      }

      // only update the model if messageBody not empty at this point
      if (messageBody.length) {
        basicMessage.messageBody = messageBody;
        this.fixHtml(message);
      }
    }
  }

  static backfillMessage(service: AlertModelService) {
    this.backfillMessageOnModel(service.alertMessage, service.isRarf);
  }

  static backfillMessageOnModel(message: MessageSettings, isRarf: boolean) {
    this.initMessage(message);
    this.fixHtml(message);

    // copy a plain text version of the html to the message field
    message.basic.message = HtmlHelper.convertHtmlToPlainText(message.basic.messageBody);

    // unless this is a RARF, null out the messageRichContent to avoid displaying the
    // rich text twice on legacy clients
    if (!isRarf) {
      message.basic.messageRichContent = null;
    }
  }

  static validate(service: AlertModelService, alert: AlertDefaults = service.alertSettings): string[] {
    const errors = [];
    if (!alert?.message?.basic?.message) errors.push('FinishMessage');
    if (alert?.message?.content?.publicContentPageRequested && 
      (!alert?.message?.content?.publicContentId || !alert?.message?.basic?.externalLink)) {
      errors.push('Alert.RichText.FinishExternalLink');
    }
    
    // ROAD-11780 - show a validation errors for truncation (character limits)
    if (alert?.channels?.textMessage?.send) {
      const error = service.textPreviewHelper?.buildValidationError(service.translation);
      if (error) errors.push(error);
    }

    // ROAD-11780 - show a validation errors for truncation (character limits)
    if (alert?.channels?.twitter?.send) {
      const message = alert.channels.twitter.message ?? AlertSocialHelper.getSocialMediaMessage(alert);
      const messageLength = CharacterCountValidator.getMessageLength(message, SocialChannel.TWITTER_LIMIT, 'twitter');
      const error = AlertChannelsHelper.buildCharacterLimitValidationError(service.translation, AlertChannelsHelper.KEY_TWITTER, messageLength, SocialChannel.TWITTER_LIMIT);
      if (error) errors.push(error);
    }

    // ROAD-11780 - show a validation errors for truncation (character limits)
    if (alert?.channels?.nextdoor?.send) {
      const message = alert.channels.nextdoor.message ?? AlertSocialHelper.getSocialMediaMessage(alert);
      const error = AlertChannelsHelper.buildCharacterLimitValidationError(service.translation, AlertChannelsHelper.KEY_NEXTDOOR, message?.length, SocialChannel.NEXTDOOR_LIMIT);
      if (error) errors.push(error);
    }

    if (alert?.channels?.microsoftTeams?.send) {
      const message = alert.channels.microsoftTeams.message ?? alert.message?.basic?.message;
      const error = AlertChannelsHelper.buildCharacterLimitValidationError(service.translation, AlertChannelsHelper.KEY_MICROSOFT_TEAMS, message?.length, AlertMicrosoftTeamsHelper.MICROSOFT_TEAMS_CHAR_LIMIT);
      if (error) errors.push(error);
    }

    return errors;
  }

  static backfillPublicContent(service: AlertModelService, message: MessageSettings = service.alertMessage) {
    // ROAD-11076 - the `publicContentPageRequested` field is relatively new -- make sure it's set correctly
    if (service?.userHasExternalLink && (message?.content?.publicContentId || message?.content?.publicContentPageRequested)) {
      message.content.publicContentPageRequested = true;
    } else {
      delete message?.content;
      delete message?.basic?.externalLink;
    }

    // ROAD-5852, ROAD-11198
    // If we apply a template with a saved public content ID and external link, or if we resend an alert
    // that had a public content ID, we want to reset those properties and fetch them fresh. This avoids 
    // the issue of potentially altering historic public web pages when new alerts are sent out.
    if (service.isEditingTemplate) return;
    const workingPublicContentId = message?.content?.publicContentId;
    const defaultsPublicContentId = service.alertDefaults?.message?.content?.publicContentId
    if (!workingPublicContentId || !defaultsPublicContentId) return;
    if (defaultsPublicContentId === workingPublicContentId) {
      delete message?.content?.publicContentId;
      delete message?.basic?.externalLink;
    }
  }

  static computePlaceholderValue(placeholder: string, 
    service: AlertModelService, 
    variables: AlertSettingsVariable[], 
    message: AlertDefaults = service.alertSettings): string {
    const fallback = `{{ ${placeholder} }}`;

    if (placeholder.toLowerCase().startsWith('survey')) {
      const found = variables.find(item => item.placeHolder == placeholder);
      if (found) {
        return found.actualValue;
      }
    }

    // Split by . or _
    const parts = placeholder.includes('.') 
      ? placeholder.split('.') 
      : placeholder.split('_');

    if (parts[0]?.toLowerCase() !== 'alert') {
      return fallback;
    }
  
    let value: unknown = message;
  
    for (let i = 1; i < parts.length; i++) {
      const part = toCamelCase(parts[i]);
  
      if (value && typeof value === 'object' && part in value) {
        value = value[part];
      } else {
        return fallback;
      }
    }
  
    return (typeof value === 'string' || typeof value === 'number')
      ? value.toString()
      : fallback;
  }

}
